import React from "react";
import FormProvider from "@providers/FormProvider";
import ModalProvider, { useModalContext } from "@providers/ModalProvider";
import { useTranslationContext } from "@providers/TranslationProvider";
import { useFormik, Form } from "formik";

import styles from "./GetInTouch.module.scss";

import Divider from "@components/Divider";
import Input from "@components/Formik/Input";
import TextArea from "@components/Formik/TextArea";
import Select from "@components/Formik/Select";
import Checkbox from "@components/Formik/Checkbox";
import Modal from "@components/Modal";
import Button from "@components/Button";
import Link from "@components/Link";

import cx from "classnames";
import _ from "lodash";

import Yup from "@utils/yup";
import { EMAIL } from "@utils/validate";
import gstyles from "@gstyles/index";
import ConstactService from "@services/ContactService";
import { useRouteContext } from "@providers/RouteProvider";
import { usePageContext } from "@providers/PageProvider";
import { contactPage } from "@utils/helper";

const ModalSuccess = () => {
  const { modalService } = useModalContext();
  const { i18n } = useTranslationContext();
  const handler = {
    onClose: () => {
      modalService.hide();
    },
  };
  return (
    <Modal
      style={{
        content: { background: "white !important" },
      }}
      isOpen={modalService.visible}
      onRequestClose={handler.onClose}
    >
      <div className={styles.modal}>
        <div className="flex gap-2">
          {gstyles.icons({
            name: "check-outline",
            size: 24,
            color: gstyles.colors.green[600],
          })}
          {i18n.t("Notification.submitSuccess")}
        </div>
      </div>
    </Modal>
  );
};
const FormView = () => {
  const { i18n } = useTranslationContext();
  const { modalService } = useModalContext();
  const { router } = useRouteContext();
  const { pageData } = usePageContext();
  const getInTouch = contactPage.formatGetInTouch(pageData);
  const form: any = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      typeOfEnquiry: "",
      hotelName: "",
      message: "",
      privacyPolicyAgreement: false,
      acceptsMarketing: true,
    },
    onSubmit: async (value) => {
      const payload = { data: value };
      const res = await ConstactService.send(payload);
      if (_.has(res, "data")) {
        modalService.addDOM(<ModalSuccess />);
      }
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().required(i18n.t("Form.required")),
      lastName: Yup.string().required(i18n.t("Form.required")),
      email: Yup.string()
        .required(i18n.t("Form.required"))
        .matches(EMAIL, i18n.t("Form.emailInvalid")),
      phone: Yup.string().required(i18n.t("Form.required")),
      message: Yup.string().required(i18n.t("Form.required")),
      privacyPolicyAgreement: Yup.boolean()
        .required(i18n.t("Form.mustBeAccept"))
        .oneOf([true], i18n.t("Form.mustBeAccept")),
      // acceptsMarketing: Yup.boolean()
      //   .required(i18n.t("Form.mustBeAccept"))
      //   .oneOf([true], i18n.t("Form.mustBeAccept")),
    }),
  });
  _.assign(form, {
    canSubmit: !form.isSubmitting && form.isValid && form.dirty,
  });
  return (
    <section data-scroll-section className={cx(styles.section)}>
      <div className={"inner"}>
        <Divider
          labelProps={{ className: styles.label }}
          label={
            <>
              <div className={styles.title}>
                {_.get(getInTouch, "title", "")}
              </div>
            </>
          }
        />
      </div>
      <div className={"inner-large"}>
        <div className={cx("ama-content text-black-1", styles.description)}>
          {_.get(getInTouch, "description", "")}
        </div>
        <FormProvider form={form}>
          <Form>
            <div className={styles.form}>
              <div className={styles.half}>
                <Input
                  name="firstName"
                  placeholder={i18n.t("Form.placeholderFirstName")}
                  required
                />
                <Input
                  name="lastName"
                  placeholder={i18n.t("Form.placeholderLastName")}
                  required
                />
                <Input
                  name="email"
                  placeholder={i18n.t("Form.placeholderEmail")}
                  required
                />
                <Input
                  name="phone"
                  placeholder={i18n.t("Form.placeholderPhone")}
                  required
                />
                <Select
                  optionCapitalize
                  name="typeOfEnquiry"
                  placeholder={i18n.t("Form.placeholderEnquiry")}
                  options={_.get(getInTouch, "typeOfEnquiry", [])}
                />
                <Select
                  optionCapitalize
                  name="hotelName"
                  placeholder={i18n.t("Form.placeholderWhichHotel")}
                  options={_.get(getInTouch, "hotels", [])}
                />
              </div>

              <TextArea
                name="message"
                placeholder={i18n.t("Form.placeholderMessage")}
                required
              />
              <div className="flex flex-col">
                <div className="flex items-center">
                  <Checkbox
                    name="privacyPolicyAgreement"
                    label={
                      <span>
                        {i18n.t("ContactUs.iConfirm")}{" "}
                        <Link
                          target={"_blank"}
                          className=" text-yellow-1"
                          href={router.toUrl("privacy", {
                            legalNoticeType: "PrivacyPolicy",
                          })}
                        >
                          {i18n.t("ContactUs.privacy")}
                        </Link>
                      </span>
                    }
                  />
                </div>
                <div className="flex items-center mt-4">
                  <Checkbox
                    name="acceptsMarketing"
                    label={
                      <span>{i18n.t("ContactUs.acceptReceiveInfomation")}</span>
                    }
                  />
                </div>
              </div>
              <div className={styles.button}>
                <Button
                  className={styles.btn}
                  disabled={!form.canSubmit}
                  onClick={form.handleSubmit}
                  type="primary"
                  dark
                  color="black"
                  label={i18n.t("Button.submit")}
                  suffix="shape-arrow"
                />
              </div>
            </div>
          </Form>
        </FormProvider>
      </div>
    </section>
  );
};
const View = (props: any) => {
  return (
    <ModalProvider>
      <FormView {...props} />
    </ModalProvider>
  );
};
export default View;
